<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useScreenSize } from "../../../utils/useScreenSize";
import IconComponent from "../common/IconComponent.vue";
import {
  DeliveryTracking,
  DeliveryTrackingSteps,
  RecordStatus,
} from "../../../types/models";
import FileUpload from "../form/FileUpload.vue";

const { isMobile } = useScreenSize();

const props = defineProps<{
  data?: any;
  recordStatus: RecordStatus[];
}>();

const delivery_tracking = ref<DeliveryTracking>();

const completedSteps = ref<DeliveryTrackingSteps[]>([]);
const exclusiveSteps = props.recordStatus.filter((step) =>
  [
    "Payment Received",
    "Waiting for Payment",
    "Awaiting Payment",
    "Payment on Delivery",
    "Credit Bill",
  ].includes(step.name)
);
const activeExclusiveStep = computed(() => {
  return completedSteps.value
    .map((tracking) => {
      return exclusiveSteps.find(
        (step) => step.id.toString() === tracking.step_id
      );
    })
    .filter(Boolean);
});

const filteredSteps = computed(() => {
  return props.recordStatus?.filter((step: RecordStatus) => {
    if (exclusiveSteps.includes(step) && activeExclusiveStep.value.length > 0) {
      return (
        activeExclusiveStep.value[0] &&
        step.name === activeExclusiveStep.value[0].name
      );
    }
    return true;
  });
});

const nextStep = computed(() => {
  return filteredSteps.value.find(
    (step) => Number(step.id) > lastCompletedStep.value
  );
});

const lastCompletedStep = computed(() => {
  return completedSteps.value.length
    ? Math.max(
        ...completedSteps.value.map((item) =>
          parseInt(item.step_id?.toString())
        )
      )
    : 0;
});

const showFullSteps = ref(false);

const isCompleted = (step: RecordStatus) => {
  return completedSteps.value.filter((e) => e.step_id == step.id).length > 0;
};

watch(
  () => props.data,
  (_newVal) => {
    delivery_tracking.value = props.data;
    completedSteps.value = delivery_tracking.value?.steps || [];
    console.log(completedSteps.value);
  }
);

onMounted(() => {
  delivery_tracking.value = props.data;
  completedSteps.value = delivery_tracking.value?.steps || [];
});
</script>

<template>
  <div class="flex w-full flex-wrap md:flex-nowrap">
    <div v-for="s in recordStatus" class="hidden">bg-[{{ s.color }}]</div>
    <template v-if="!isMobile">
      <div
        v-for="step in filteredSteps"
        :key="step.id"
        class="flex-1 relative mb-8 md:mb-0 flex items-center justify-between p-4 rounded-lg"
        :class="[
          isCompleted(step) ||
          step.id === nextStep?.id ||
          step.name == 'Delivery Requested'
            ? `bg-[${step.color}]`
            : `bg-gray-700 bg-gray-200`,
          step.id === nextStep?.id ? `animate-blink` : ``,
        ]"
        :style="{
          backgroundColor:
            isCompleted(step) ||
            step.id === nextStep?.id ||
            step.name == 'Delivery Requested'
              ? `${step.color}`
              : `bg-gray-700 bg-gray-200`,
        }"
      >
        <div class="flex items-center">
          <!-- Icon -->
          <IconComponent
            :name="step.icon"
            class="w-5 h-5 mr-2"
            :class="
              isCompleted(step)
                ? 'text-white'
                : 'text-gray-500 dark:text-gray-400'
            "
          />

          <!-- Step text -->
          <span
            class="relative z-20 text-sm font-medium"
            :class="
              isCompleted(step)
                ? 'text-white'
                : 'text-gray-500 dark:text-gray-400'
            "
          >
            {{ step.name }}
          </span>
        </div>

        <!-- Green Checkmark for Completed Steps -->
        <IconComponent
          name="CheckCircle"
          v-if="isCompleted(step)"
          class="w-6 h-6 text-white"
        />
      </div>
    </template>
    <template v-else>
      <div class="w-full">
        <div
          v-if="!showFullSteps"
          class="flex items-center justify-between p-4 mb-4 rounded-lg animate-blink"
          :class="[
            completedSteps.some((step) => step.step_id === nextStep?.id)
              ? `bg-[${nextStep?.color}]`
              : `bg-[${nextStep?.color}]`,
          ]"
        >
          <div class="flex items-center">
            <IconComponent
              :name="nextStep?.icon"
              class="w-5 h-5 mr-2"
              :class="
                completedSteps.some((step) => step.step_id === nextStep?.id)
                  ? 'text-white'
                  : 'text-gray-500 dark:text-gray-400'
              "
            />
            <span
              class="text-sm font-medium"
              :class="
                completedSteps.some((step) => step.step_id === nextStep?.id)
                  ? 'text-white'
                  : 'text-gray-500 dark:text-gray-400'
              "
            >
              {{ nextStep?.name }}
            </span>
          </div>
          <!-- Icon button to show more steps -->
          <button @click="showFullSteps = !showFullSteps" class="text-white">
            <IconComponent name="Ellipsis" class="w-5 h-5" />
          </button>
        </div>
      </div>
      <div v-if="showFullSteps" class="w-full">
        <div
          v-for="step in filteredSteps"
          :key="step.id"
          class="flex items-center justify-between p-4 mb-4 rounded-lg"
          :class="[
            isCompleted(step) ? `bg-[${step.color}]` : `bg-[${step.color}]`,
            step.id === nextStep?.id ? `animate-blink` : ``,
          ]"
        >
          <div class="flex items-center">
            <IconComponent
              :name="step.icon"
              class="w-5 h-5 mr-2"
              :class="
                isCompleted(step)
                  ? 'text-white'
                  : 'text-gray-500 dark:text-gray-400'
              "
            />
            <span
              class="text-sm font-medium"
              :class="
                isCompleted(step)
                  ? 'text-white'
                  : 'text-gray-500 dark:text-gray-400'
              "
            >
              {{ step.name }}
            </span>
          </div>
          <div v-if="isCompleted(step)">
            <IconComponent name="CheckCircle" class="w-5 h-5 text-white" />
          </div>
        </div>
        <div class="flex justify-center mb-2">
          <button @click="showFullSteps = !showFullSteps" class="text-white">
            <IconComponent name="CircleChevronUp" class="w-5 h-5" />
          </button>
        </div>
      </div>
    </template>
    <div v-if="delivery_tracking?.attachment" style="padding: 5px;
    background: #91f391;
    border-radius: 10px;">
      <FileUpload
        v-model="delivery_tracking.attachment"
        :readonly="true"
        size="mini"
        label=""
        path="delivery_tracking_attachments"
        :read-only="true"
        :show-label="false"
        :show-upload-button="false" />
    </div>
  </div>
</template>

<style scoped>
@keyframes blink {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
}

.animate-blink {
  animation: blink 1.2s infinite;
}
</style>
